/**
 * BindingRow — one sortable row in the Binding Editor's direct-bindings
 * table (PIM Wave P1 Task 8, design notes Surface 4). Mirrors
 * `attribute-fields/components/OptionsEditor.tsx`'s `OptionRow` dnd-kit
 * idiom (`useSortable`, transform/opacity-only lift, `GripVertical` handle)
 * — reorder persistence itself lives in the parent (`BindingEditorPage`),
 * which sends the PUT-exhaustive `ordered_binding_ids` list, NOT a per-row
 * PATCH (recon A13/brief: bindings have a batch reorder route, unlike
 * options).
 *
 * Requirement: `Segmented` (required/recommended/optional), instant PATCH,
 * optimistic with rollback-to-previous + error toast on failure (design
 * notes "instant PATCH, optimistic"). `Segmented` has no built-in
 * accessible name (`packages/ui/src/Segmented.tsx` — no `aria-label` prop),
 * so it is wrapped in `role="group"` + `aria-label` per the established
 * codebase idiom (`FeedbackQueue.tsx`, `OocQueue.tsx`).
 *
 * Weight override: blank = inherit the field's own `matching_weight`
 * (shown as the input's placeholder — brief: "shows the field's own
 * matching_weight muted as placeholder/effective value"); commits on blur
 * only (brief: "commit on blur via PATCH: blank→null, value→string"), never
 * per keystroke (global rule: validate/commit on blur, not per keystroke).
 *
 * Remove: NOT a local confirm — the "remove" button here only calls
 * `onRequestRemove`; the actual confirm `BottomSheet` + DELETE mutation live
 * in `BindingEditorPage` (ONE sheet at the page level, mirroring
 * `CategoryListPage`/`FieldLibraryPage`'s archive-confirm idiom, rather than
 * one `BottomSheet` instance per row).
 *
 * Visibility-rule indicator (Task 9): an icon button — `Eye` + accented
 * treatment when `binding.visibility_rule` is set, `EyeOff` + muted when
 * not — opens `VisibilityRuleBuilder` (a per-row `BottomSheet`, UNLIKE the
 * shared remove-confirm sheet above: each row's builder needs its own
 * binding-scoped draft state, so it is NOT lifted to the page). Candidate
 * controlling fields (`candidateFields`/`resolutionFields`) and their
 * loading state (`candidatesLoading`) are computed ONCE at
 * `BindingEditorPage` and threaded down here — this row never re-fetches.
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Archive, Eye, EyeOff, GripVertical, Trash2 } from "lucide-react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { GPill, Segmented } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import {
  BINDING_REQUIREMENT_VALUES,
  type CategoryBinding,
} from "@/features/admin/category-bindings/api";
import { useUpdateCategoryBinding } from "@/features/admin/category-bindings/hooks";
import { VisibilityRuleBuilder } from "@/features/admin/category-bindings/components/VisibilityRuleBuilder";

const ICON_BUTTON_CLASS =
  "grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40";

const WEIGHT_INPUT_CLASS =
  "h-9 w-24 shrink-0 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-2 text-right text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-60";

export interface BindingRowProps {
  categoryId: string;
  binding: CategoryBinding;
  /** Undefined only if the field catalogue hasn't loaded yet or the field
   * was hard-deleted server-side out of band — renders the raw id as a
   * fallback rather than crashing the row. */
  field: AttributeField | undefined;
  dragDisabled: boolean;
  onRequestRemove: (binding: CategoryBinding) => void;
  /** NON-archived select/boolean fields on the category's effective chain —
   * passed through unchanged to `VisibilityRuleBuilder` (Task 9) as the
   * NEW-rule pick list, which excludes this row's own field. */
  candidateFields: AttributeField[];
  /** ALL select/boolean fields on the category's effective chain, archived
   * fields INCLUDED — passed through unchanged to `VisibilityRuleBuilder`
   * (post-review fix) to resolve a PERSISTED rule's controlling field for
   * display even when that field is archived. */
  resolutionFields: AttributeField[];
  /** True while the field drain / ancestor-binding fan-out feeding
   * `candidateFields`/`resolutionFields` is still in flight (post-review
   * fix) — passed through unchanged to `VisibilityRuleBuilder`. */
  candidatesLoading: boolean;
}

export function BindingRow({
  categoryId,
  binding,
  field,
  dragDisabled,
  onRequestRemove,
  candidateFields,
  resolutionFields,
  candidatesLoading,
}: BindingRowProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: binding.id,
    disabled: dragDisabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  const requirementMutation = useUpdateCategoryBinding();
  const weightMutation = useUpdateCategoryBinding();

  const fieldName = field?.name_ru ?? binding.field_id;
  const typeMeta = field ? fieldTypeMeta(field.field_type) : undefined;

  // --- Requirement: optimistic instant PATCH, rollback + toast on error. ---
  const [requirement, setRequirement] = useState(binding.requirement);
  useEffect(() => setRequirement(binding.requirement), [binding.requirement]);

  const handleRequirementChange = (next: string): void => {
    const previous = requirement;
    setRequirement(next);
    requirementMutation.mutate(
      { categoryId, bindingId: binding.id, body: { requirement: next } },
      {
        onError: () => {
          setRequirement(previous);
          toast.error(t("error.generic"));
        },
      },
    );
  };

  // --- Weight override: blank draft = inherit; commit on blur only. ---
  const [weightDraft, setWeightDraft] = useState(binding.matching_weight_override ?? "");
  useEffect(
    () => setWeightDraft(binding.matching_weight_override ?? ""),
    [binding.matching_weight_override],
  );

  const commitWeight = (): void => {
    const trimmed = weightDraft.trim();
    const next = trimmed === "" ? null : trimmed;
    if (next === (binding.matching_weight_override ?? null)) return;
    weightMutation.mutate(
      { categoryId, bindingId: binding.id, body: { matching_weight_override: next } },
      {
        onError: () => {
          setWeightDraft(binding.matching_weight_override ?? "");
          toast.error(t("error.generic"));
        },
      },
    );
  };

  // --- Visibility rule indicator + sheet (Task 9). ---
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const hasVisibilityRule = binding.visibility_rule !== null;

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid="pim-binding-row"
      className="flex flex-wrap items-center gap-3 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3 motion-reduce:!transition-none"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        disabled={dragDisabled}
        aria-label={t("pim.bindings.row.dragHandle", { name: fieldName })}
        data-testid={`pim-binding-drag-${binding.id}`}
        className="inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded-[var(--g-radius-sm)] text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <GripVertical size={16} strokeWidth={1.75} aria-hidden />
      </button>

      <div className="flex min-w-[10rem] flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{fieldName}</span>
          {typeMeta ? (
            <GPill tone="neutral" size="sm">
              {t(typeMeta.labelKey)}
            </GPill>
          ) : null}
          {field?.is_archived ? (
            <GPill
              tone="neutral"
              size="sm"
              leading={<Archive size={12} strokeWidth={1.75} aria-hidden />}
            >
              {t("pim.bindings.row.archivedField")}
            </GPill>
          ) : null}
        </div>
        <span className="truncate font-mono text-xs text-[var(--g-secondary)]">
          {field?.slug ?? binding.field_id}
        </span>
      </div>

      <div
        role="group"
        aria-label={t("pim.bindings.row.requirementLabel", { name: fieldName })}
        data-testid={`pim-binding-requirement-${binding.id}`}
      >
        <Segmented
          options={BINDING_REQUIREMENT_VALUES.map((value) => ({
            value,
            label: t(`pim.bindings.requirement.${value}`),
          }))}
          value={requirement}
          onChange={handleRequirementChange}
        />
      </div>

      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        data-testid={`pim-binding-weight-${binding.id}`}
        aria-label={t("pim.bindings.row.weightOverrideLabel", { name: fieldName })}
        value={weightDraft}
        placeholder={field?.matching_weight ?? undefined}
        disabled={weightMutation.isPending}
        onChange={(event) => setWeightDraft(event.target.value)}
        onBlur={commitWeight}
        className={WEIGHT_INPUT_CLASS}
      />

      <button
        type="button"
        data-testid={`pim-binding-visibility-${binding.id}`}
        aria-label={
          hasVisibilityRule
            ? t("pim.bindings.visibility.indicatorSet", { name: fieldName })
            : t("pim.bindings.visibility.indicatorUnset", { name: fieldName })
        }
        onClick={() => setVisibilityOpen(true)}
        className={[
          "grid h-9 w-9 shrink-0 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40",
          hasVisibilityRule
            ? "bg-[var(--g-primary)]/10 text-[var(--g-primary)] hover:bg-[var(--g-primary)]/20"
            : "text-[var(--g-secondary)] hover:bg-[var(--g-elevated)]",
        ].join(" ")}
      >
        {hasVisibilityRule ? (
          <Eye size={16} strokeWidth={1.75} aria-hidden />
        ) : (
          <EyeOff size={16} strokeWidth={1.75} aria-hidden />
        )}
      </button>

      <VisibilityRuleBuilder
        open={visibilityOpen}
        onClose={() => setVisibilityOpen(false)}
        categoryId={categoryId}
        binding={binding}
        ownFieldName={fieldName}
        candidateFields={candidateFields}
        resolutionFields={resolutionFields}
        candidatesLoading={candidatesLoading}
      />

      <button
        type="button"
        data-testid={`pim-binding-remove-${binding.id}`}
        aria-label={t("pim.bindings.row.remove", { name: fieldName })}
        onClick={() => onRequestRemove(binding)}
        className={ICON_BUTTON_CLASS}
      >
        <Trash2 size={16} strokeWidth={1.75} aria-hidden />
      </button>
    </li>
  );
}

export default BindingRow;
